/**
 * Rapport-klient för Lyktan (S-08-04).
 *
 * Hard-rules från CLAUDE.md / UI-kontrakt vy 8:
 *  - Cookie-baserad auth — `credentials: 'include'` på alla fetch-anrop.
 *  - Backend exponerar inte rapportör-identitet (user_id) i normalvyn,
 *    så denna klient definierar inte heller fältet i AdminReport-typen.
 *  - Alla användartexter på svenska. Maskinvärden från backend
 *    översätts av reportLabels.ts.
 *  - PATCH-anrop måste alltid skicka `reason_code` enligt S-08-01-
 *    kontraktet — Eko-värdet är hårdkodat (HANDLED_REASON_CODE) och
 *    Avvisa/Markera-ny-igen kommer från fasta listor.
 *
 * Felmeddelanden mappas från opaque error-koder till svensk användartext.
 */

import type { ReportCategory, ReportStatus } from './reportLabels';

export type AdminReport = {
  id: string;
  category: ReportCategory;
  latitude: number;
  longitude: number;
  reportedAt: string;
  status: ReportStatus;
  statusUpdatedAt: string | null;
  /** Teknisk referens (icke-klickbar i MVP1, A13/admin-walk-vy = blocker). */
  walkId: string | null;
};

export type ListReportsParams = {
  status?: ReportStatus;
  category?: ReportCategory;
  limit?: number;
  offset?: number;
};

export type ListReportsResult =
  | { kind: 'ok'; items: AdminReport[]; limit: number; offset: number }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type UpdateStatusResult =
  | { kind: 'ok'; report: AdminReport }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

const ADMIN_BASE = '/api/admin/reports';

function messageForError(code: string): string {
  switch (code) {
    case 'invalid_status':
      return 'Ogiltigt status-värde.';
    case 'invalid_category':
      return 'Ogiltig kategori.';
    case 'invalid_pagination':
      return 'Ogiltig sidnumrering.';
    case 'invalid_reason_code':
      return 'Motiveringen matchar inte vald status.';
    case 'invalid_id':
      return 'Ogiltigt rapport-id.';
    case 'not_found':
      return 'Rapporten kunde inte hittas.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawAdminReport = {
  id: string;
  category: string;
  latitude: number;
  longitude: number;
  reported_at: string;
  status: string;
  status_updated_at: string | null;
  walk_id: string | null;
};

type RawListResponse = {
  items: RawAdminReport[];
  limit: number;
  offset: number;
};

function mapRawReport(raw: RawAdminReport): AdminReport {
  return {
    id: raw.id,
    category: raw.category as ReportCategory,
    latitude: raw.latitude,
    longitude: raw.longitude,
    reportedAt: raw.reported_at,
    status: raw.status as ReportStatus,
    statusUpdatedAt: raw.status_updated_at,
    walkId: raw.walk_id,
  };
}

/**
 * GET /api/admin/reports — listning med filter.
 *
 * Backend tillåter filter på `status` och `category` (en av varje åt
 * gången enligt S-08-01). Datumintervall-filter görs klient-side i
 * MVP1 — backend har inte stöd för det än, och listan har max 200 rader
 * så det fungerar i Hisings Backas pilot-skala.
 */
export async function listReports(params: ListReportsParams = {}): Promise<ListReportsResult> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.category) search.set('category', params.category);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));

  const url = search.toString().length > 0 ? `${ADMIN_BASE}?${search.toString()}` : ADMIN_BASE;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.ok) {
    try {
      const body = (await response.json()) as RawListResponse;
      return {
        kind: 'ok',
        items: body.items.map(mapRawReport),
        limit: body.limit,
        offset: body.offset,
      };
    } catch {
      return { kind: 'failure', message: messageForError('default') };
    }
  }
  const errBody = await safeJson(response);
  return { kind: 'failure', message: messageForError(errBody?.error ?? 'default') };
}

/**
 * PATCH /api/admin/reports/{id} — uppdatera status med fast reason_code.
 *
 * Klienten skickar alltid `reason_code` i body. För Eko (handled) är
 * värdet hårdkodat i anroparen. För Avvisa/Markera-ny-igen kommer det
 * från fasta listor i reportLabels.ts. Server validerar mot S-08-01-
 * tabellen och returnerar 400 invalid_reason_code vid mismatch.
 */
export async function updateReportStatus(
  id: string,
  status: ReportStatus,
  reasonCode: string,
): Promise<UpdateStatusResult> {
  let response: Response;
  try {
    response = await fetch(`${ADMIN_BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason_code: reasonCode }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.ok) {
    try {
      const raw = (await response.json()) as RawAdminReport;
      return { kind: 'ok', report: mapRawReport(raw) };
    } catch {
      return { kind: 'failure', message: messageForError('default') };
    }
  }
  const errBody = await safeJson(response);
  return { kind: 'failure', message: messageForError(errBody?.error ?? 'default') };
}
